import * as C from '../common/constants';
import SelectColors from './SelectColors';
import SelectPaintScheme from './SelectPaintScheme';
import SelectBlades from './SelectBlades';
import SelectEngine from './SelectEngine';
import { COLORS, FONTS, FONT_WEIGHT } from '../common/theme';
import { makeStyles, createStyles, StyleRules } from '@mui/styles';
import { useSelector, useDispatch } from 'react-redux';
import { useState, useEffect } from 'react';
import Header from './Header';
import CloseIcon from '@mui/icons-material/Close';
import clsx from 'clsx';
import { useTranslations } from '../common/hooks/useTranslations';
import { storeData, clearData } from '../common/app/actions';
import { EMenus, ESides } from '../common/enums';
import { useSwipeable } from 'react-swipeable';
import ButtonScreenshot from './ButtonScreenshot';

const useStyles = makeStyles(
  (theme): StyleRules =>
    createStyles({
      boxStyle: {
        position: 'fixed',
        zIndex: '1000',
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
      },
      boxActiveStyle: {
        // pointerEvents: 'all',
      },
      bgStyle: {
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
        // background: 'rgba(0, 0, 0, 0.5)',
        opacity: 1,
        transition: '0.3s ease',
        pointerEvents: 'none',
      },
      bgHiddenStyle: {
        opacity: 0,
        pointerEvents: 'none',
      },
      menuStyle: {
        position: 'absolute',
        zIndex: '1100',
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        padding: '60px 20px 20px 20px',
        overflow: 'auto',
        background: COLORS.black, // COLORS.menuGrey,
        maxWidth: '300px',
        transition: '0.5s ease',
        pointerEvents: 'all',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      },
      menuLeftStyle: {
        left: 0,
        top: 0,
        transform: 'translate3d(-100%, 0, 0)',
      },
      menuLeftVisibleStyle: {
        left: 0,
        top: 0,
        transform: 'translate3d(0, 0, 0)',
      },
      menuRightStyle: {
        right: 0,
        top: 0,
        transform: 'translate3d(100%, 0, 0)',
      },
      menuRightVisibleStyle: {
        right: 0,
        top: 0,
        transform: 'translate3d(0, 0, 0)',
      },
      iconStyle: {
        position: 'absolute',
        right: 0,
        top: 0,
        padding: '10px',
        pointerEvents: 'auto',
        color: COLORS.white,
        cursor: 'pointer',
        '@media (hover: hover) and (pointer: fine)': {
          '&:hover': {
            opacity: 0.7,
            transition: '0.5s ease',
          },
        },
      },
      buttonStyle: {
        width: '100%',
        cursor: 'pointer',
        '@media (hover: hover) and (pointer: fine)': {
          '&:hover': {
            // opacity: 0.7,
            // transition: '0.5s ease',
          },
        },
      },
      logoStyle: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginBottom: '40px',
        '& > svg': {
          width: '100%',
          // height: '36px',
          fill: COLORS.white,
        },
        '& > span': {
          color: COLORS.lightGrey,
          fontSize: '11px',
        },
      },
      '@media (max-width: 290px)': {
        logoStyle: {
          marginBottom: '30px',
          '& > span': {
            fontSize: '8px',
          },
        },
      },
      '@media only screen and (max-height: 840px)': {
        menuStyle: {
          padding: '40px 20px 10px 20px',
        },
      },
      '@media only screen and (max-height: 740px)': {
        logoStyle: {
          marginBottom: '20px',
        },
      },
    })
);

const MenuWrapper = ({ mainRef }): JSX.Element => {
  const classes = useStyles();
  const dispatch: any = useDispatch();

  const appData = useSelector((state: any) => state.app[C.APP_DATA]);
  const paintData = appData?.[EMenus.PAINT];

  const menuSide = useSelector((state: any) => state.app[C.SET_MENU]);
  const isMenuVisible = Boolean(menuSide);

  const onSwipe = (direction: ESides) => {
    if (
      (menuSide === ESides.RIGHT && direction === ESides.RIGHT) ||
      (menuSide === ESides.LEFT && direction === ESides.LEFT)
    ) {
      dispatch(clearData(C.SET_MENU));
    }
  };

  const handlers = useSwipeable({
    onSwipedLeft: () => onSwipe(ESides.LEFT),
    onSwipedRight: () => onSwipe(ESides.RIGHT),
    // swipeDuration: 50,
    preventScrollOnSwipe: true,
    trackMouse: true,
  });

  useEffect(() => {
    if (paintData) {
      // set engine
      // const [firstEngineData] = labelData.menu[EMenus.ENGINE].items;
      // dispatch(storeData(firstEngineData, C.SET_ENGINE));

      // set propeller blade type
      dispatch(storeData('3', C.SET_BLADES));

      // set classic paint scheme
      const [classicPaint] = paintData;
      dispatch(storeData(classicPaint, C.SET_PAINT));

      // set paint scheme colors
      dispatch(storeData(classicPaint.colors, C.SET_COLORS));

      const firstVisibleColorIndex = classicPaint.colors.findIndex(
        (item) => item.visible
      );
      // const firstColor = classicPaint.colors[firstVisibleColorIndex]?.default;
      // dispatch(
      //   storeData(
      //     { nr: firstVisibleColorIndex + 1, color: firstColor },
      //     C.SET_SELECTED_COLOR
      //   )
      // );
    }
  }, [paintData]);

  if (!appData || !paintData) {
    return null;
  }

  const onClick = (event: React.SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();

    dispatch(clearData(C.SET_MENU));
  };

  return (
    <div
      className={clsx(classes.boxStyle, menuSide && classes.boxActiveStyle)}
      {...handlers}
    >
      <div
        className={clsx(
          classes.bgStyle,
          !isMenuVisible && classes.bgHiddenStyle
        )}
        // onClick={onClick}
      />
      <div
        className={clsx(
          classes.menuStyle,
          classes.menuLeftStyle,
          menuSide === 'left' && classes.menuLeftVisibleStyle
        )}
      >
        <div className={classes.topStyle}>
          <div className={classes.logoStyle}>
            <Header />
          </div>
          <SelectPaintScheme />
          <SelectColors />
        </div>
        <div className={classes.buttonStyle}>
          <ButtonScreenshot mainRef={mainRef} />
        </div>
        <CloseIcon
          onClick={onClick}
          fontSize={'large'}
          className={classes.iconStyle}
        />
      </div>
      {/* <div
        className={clsx(
          classes.menuStyle,
          classes.menuRightStyle,
          menuSide === 'right' && classes.menuRightVisibleStyle
        )}
      >
        <SelectEngine labelData={labelData} />
        <SelectBlades labelData={labelData} />
        <CloseIcon
          onClick={onClick}
          fontSize={'large'}
          className={classes.iconStyle}
        />
      </div> */}
    </div>
  );
};

export default MenuWrapper;
